import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '../../lib/api';
import { formatCurrency, copyToClipboard } from '../../lib/utils';
import { usePublisherLookup } from '../../hooks/useLookups';
import { Link as LinkIcon, Copy, ExternalLink, AlertCircle } from 'lucide-react';
import { Card } from '../ui/Card';

interface TrackingLink {
  id: number;
  campaignId: number;
  domain: string;
  urlTemplate: string;
  isActive: boolean;
}

interface LinkedSourceCardProps {
  publisherId: number | null;
  subId: string | null;
  payoutApproved: number;
  clicksQuality?: number;
}

export function LinkedSourceCard({
  publisherId,
  subId,
  payoutApproved,
}: LinkedSourceCardProps) {
  const publisherLookup = usePublisherLookup();

  // BUGFIX: useQuery must run BEFORE the early return — a conditional hook
  // after `return null` crashes React when the drawer transitions from an
  // unlinked row (publisherId=null) to a linked one (hook count changes).
  const trackingLinks = useQuery({
    queryKey: ['tracking-links', publisherId],
    queryFn: () => apiClient.get<TrackingLink[]>('/api/v1/tracking-links'),
    enabled: publisherId != null && subId != null,
  });

  // Only render if publisherId is not null (linked source)
  if (publisherId == null || subId == null) {
    return null;
  }

  // Negotiated rate from the publishers table (e.g. $0.80/conversion) —
  // NOT payout÷clicks, which reads $0.00 whenever AE click data is absent.
  const payoutRate = publisherLookup.getPayoutValue(publisherId);

  const getSubstitutedUrl = (urlTemplate: string): string => {
    return urlTemplate.replace('{sub_id}', subId);
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await copyToClipboard(url);
      // Optional: show toast notification here
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <LinkIcon size={16} className="text-neon-magenta" />
        <h4 className="text-[10px] font-display uppercase tracking-wider text-neon-magenta">
          Linked Source · {publisherLookup.getName(publisherId)}
        </h4>
      </div>

      {/* Payout Info */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] font-mono text-text-tertiary uppercase">Payout Rate</div>
          <div className="text-neon-yellow font-display font-bold numeric">
            {payoutRate != null ? `${formatCurrency(payoutRate)} per conversion` : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono text-text-tertiary uppercase">Total Payout</div>
          <div className="text-neon-green font-display font-bold numeric">
            {formatCurrency(payoutApproved)}
          </div>
        </div>
      </div>

      {/* Get Links Button */}
      <div className="pt-2 border-t border-border-subtle">
        <div className="text-[10px] font-mono text-text-secondary mb-2">
          Tracking Links ({trackingLinks.data?.length ?? 0} domains)
        </div>

        {trackingLinks.isLoading ? (
          <p className="text-[11px] font-mono text-text-tertiary italic">
            loading tracking links...
          </p>
        ) : trackingLinks.data && trackingLinks.data.length > 0 ? (
          <div className="space-y-2">
            {trackingLinks.data.map((link) => {
              const substitutedUrl = getSubstitutedUrl(link.urlTemplate);
              return (
                <div
                  key={link.id}
                  className="flex items-center gap-2 p-2 bg-bg-secondary rounded border border-border-subtle"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-neon-cyan font-mono text-xs truncate">
                        {link.domain}
                      </span>
                      {!link.isActive && (
                        <span className="text-[9px] font-mono text-text-tertiary uppercase">
                          paused
                        </span>
                      )}
                    </div>
                    <a
                      href={substitutedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] font-mono text-text-tertiary hover:text-neon-cyan truncate block mt-0.5"
                      title={substitutedUrl}
                    >
                      {substitutedUrl}
                    </a>
                  </div>

                  <button
                    onClick={() => handleCopyUrl(substitutedUrl)}
                    className="flex items-center gap-1 px-2 py-1 bg-bg-tertiary hover:bg-neon-cyan/10 border border-border-subtle hover:border-neon-cyan/40 rounded text-[10px] font-mono text-text-secondary transition-all"
                    title="Copy URL"
                  >
                    <Copy size={10} />
                    <span>Copy</span>
                  </button>

                  <a
                    href={substitutedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 bg-bg-tertiary hover:bg-neon-magenta/10 border border-border-subtle hover:border-neon-magenta/40 rounded text-[10px] font-mono text-text-secondary transition-all"
                    title="Open in new tab"
                  >
                    <ExternalLink size={10} />
                  </a>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-3 border border-neon-yellow/40 bg-neon-yellow/5 rounded text-left">
            <div className="flex items-start gap-2">
              <AlertCircle size={14} className="text-neon-yellow mt-0.5 flex-shrink-0" />
              <div className="text-xs font-mono text-neon-yellow">
                No tracking links found for this publisher.{' '}
                <Link
                  to="/publishers"
                  className="underline hover:text-neon-cyan"
                >
                  Configure tracking links
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
